<template>
    <div class="flex mt-24 gap-12" :data-section-id="section?.id">
        <div v-if="section && cards.length" class="min-w-312 max-w-312 text-black space-y-24">
            <div>
                <div class="flex">
                    <div
                        v-if="section.icon"
                        class="flex items-center w-full max-w-24 max-h-36 mr-8"
                    >
                        <img :src="section.icon" alt="" class="w-full h-full" :style="{ filter: `brightness(0) saturate(100%) invert(9%) sepia(10%) saturate(489%) hue-rotate(294deg) brightness(97%) contrast(91%)` }">
                    </div>
                    <p class="text-heading-2 font-semibold">{{ title }}</p>
                </div>

                <language-dropdown
                    :activeItem="activeLocale"
                    class="mt-8"
                    :items="activeLocales"
                    v-if="section.enable_locales"
                    @input="activeLocale = $event"
                />
            </div>

            <div class="ml-24 text-body-sm max-h-320 text-ellipsis overflow-y-clip">
                <p v-html="description"></p>
            </div>

            <p class="text-body-sm">{{ $t('share_job.section_description') }}</p>
        </div>

        <div class="relative overflow-x-hidden -mt-20 -ml-20">
            <div
                v-if="scrollPosition !== 0"
                :id="`btn-left-${sectionId}`"
                class="absolute left-40 top-1/2 transform -translate-y-1/2 z-60 flex shrink-0 justify-center items-center arrow-shadow rounded-full bg-white w-48 h-48 cursor-pointer"
                @click="scrollToLeft()"
            >
                <svg-icon
                    class="max-w-24 max-h-24 text-black rotate-180"
                    type="keyboard-arrow-right"
                />
            </div>
            <div
                :id="`scroll-section-${sectionId}`"
                class="flex relative text-18 gap-32 overflow-x-auto overflow-y-visible scrollbar-hide p-20"
                @scroll="handleScroll($event)"
            >
                <ShareJobCardItem
                    v-for="card in sortedCards"
                    :key="card.id"
                    :cardId="card.id"
                    :job="job"
                    :showFavoriteButton="true"
                    :activeLocale="activeLocale"
                    @selectedCard="$emit('selectedCard', $event)"
                />
            </div>
            <div
                v-if="canScroll && !isAbsoluteRight"
                :id="`btn-left-${sectionId}`"
                class="absolute right-40 top-1/2 transform -translate-y-1/2 z-60 flex justify-center items-center arrow-shadow rounded-full bg-white w-48 h-48 cursor-pointer"
                @click="scrollToRight()"
            >
                <svg-icon
                    class="max-w-24 max-h-24 text-black]"
                    type="keyboard-arrow-right"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ShareJobCardItem from './ShareJobCardItem.vue';
import ShareJobSection from '../models/ShareJobSection';
import LanguageDropdown from './ui/LanguageDropdown.vue';
import {mapState, mapActions} from 'pinia';
import {useMainStore} from '../state/store';
import Job from '../models/Job';
import SvgIcon from "./ui/SvgIcon.vue";

export default {
    components: {
        ShareJobCardItem,
        LanguageDropdown,
        SvgIcon,
    },
    props: {
        section: ShareJobSection,
        job: Job,
        cards: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            error: null,
            activeLocale: null,
            userFavorites: [],
            scrollPosition: 0,
            isAbsoluteRight: false,
            canScroll: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'selectedLocale']),
        locale() {
            return this.selectedLocale;
        },
        activeLocales() {
            let activeLocales = [];

            for (let locale in this.currentCompany.jobsite.active_languages) {
                activeLocales.push(this.currentCompany.jobsite.active_languages[locale]);
            }

            return activeLocales.map(locale => ({
                label: this.$t('languages.' + locale + '.title'),
                value: locale,
            })).sort((a, b) => a.label.localeCompare(b.label));
        },
        sortedCards() {
            return this.cards
                .filter(card => !(card.subscription_type === 'vdab' && this.job.countryCode !== 'BE'))
                .sort((a, b) => a.order - b.order)
                .sort((a, b) => b.isFavorited - a.isFavorited);
        },
        title() {
            let label = this.section.title;
            if (label[this.locale]) {
                return label[this.locale];
            } else if (label['en']) {
                return label['en'];
            } else {
                for (const [key, value] of Object.entries(label)) {
                    if (value) {
                        return value;
                    }
                }
            }
        },
        description() {
            let label = this.section.description;
            if (label[this.locale]) {
                return label[this.locale];
            } else if (label['en']) {
                return label['en'];
            } else {
                for (const [key, value] of Object.entries(label)) {
                    if (value) {
                        return value;
                    }
                }
            }
        },
        sectionId() {
            return this.section?.id ?? Math.random().toString(36).substring(7);
        }
    },
    methods: {
        handleScroll(e) {
            const el = e.target;
            this.scrollPosition = el.scrollLeft;
            this.isAbsoluteRight = el.scrollLeft + el.clientWidth === el.scrollWidth;
        },
        scrollToLeft() {
            const el = document.getElementById(`scroll-section-${this.sectionId}`);
            el.scrollLeft -= el.clientWidth;
            this.scrollPosition = el.scrollLeft;
        },
        scrollToRight()  {
            const el = document.getElementById(`scroll-section-${this.sectionId}`);
            el.scrollLeft += el.clientWidth;
            this.scrollPosition = el.scrollLeft;
        },
        setCanScroll() {
            const el = document.getElementById(`scroll-section-${this.sectionId}`);
            this.canScroll = el?.scrollWidth > el?.clientWidth;
            return this.canScroll;
        }
    },
    created() {
        this.activeLocale = this.locale;
    },
    mounted() {
        this.$nextTick(() => {
            this.setCanScroll();
        });
    },
}
</script>

<style scoped>

p >>> ul {
    list-style-type: disc !important;
}

.arrow-shadow {
    box-shadow: 4px 8px 16px 2px rgba(0, 0, 0, 0.1);
}

</style>
